import { ApplicationTypes } from '@/models/ApplicationType';
import { ProgramNames } from '@/models/Program';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import useEmailRecord from '@/hooks/useEmailRecord';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AdminApplicationExportSection from './forms/admin-application-export/AdminApplicationExportSection';
import NewUserJoinedPartnerGroupSection from './forms/new-user-joined-partner-group/NewUserJoinedPartnerGroupSection';
import NewReviewerInvitationSection from './forms/new-reviewer-invitation/NewReviewerInvitationSection';
import TreeLineReceiptSection from './forms/tree-line-receipt/TreeLineReceiptSection';
import PartnerExportSection from './forms/partner-export/PartnerExportSection';
import ApplicationStatusSection from './forms/application-status/ApplicationStatusSection';
import { AdminApplicationExportSubmitState } from './forms/admin-application-export/types';
import { NewUserJoinedPartnerGroupSubmitState } from './forms/new-user-joined-partner-group/types';
import { NewReviewerInvitationSubmitState } from './forms/new-reviewer-invitation/types';
import { TreeLineReceiptSubmitState } from './forms/tree-line-receipt/types';
import { PartnerExportSubmitState } from './forms/partner-export/types';
import { ApplicationStatusSubmitState } from './forms/application-status/types';
import { PaymentType } from '@/models/Payment';

type EmailTypeOption = {
  id: number | string;
  name: string;
};

type EmailTypeKey =
  | 'admin-application-export'
  | 'new-user-joined-partner-group'
  | 'new-reviewer-invitation'
  | 'tree-line-receipt'
  | 'partner-export'
  | 'application-status';

const localeOptions = [
  { value: 'enUS', label: 'English (enUS)' },
  { value: 'esES', label: 'Español (esES)' },
  { value: 'frFR', label: 'Français (frFR)' },
  { value: 'ptBR', label: 'Português (ptBR)' },
  { value: 'itIT', label: 'Italiano (itIT)' },
] as const;

const DEFAULT_LOCALE = localeOptions[0].value;

function normalizeEmailTypeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function resolveEmailTypeKey(name: string): EmailTypeKey | null {
  const normalized = normalizeEmailTypeName(name);

  if (normalized.includes('adminapplicationexport')) return 'admin-application-export';
  if (normalized.includes('newuserjoinedpartnergroup')) return 'new-user-joined-partner-group';
  if (normalized.includes('newreviewerinvitation')) return 'new-reviewer-invitation';
  if (normalized.includes('treelinereceipt')) return 'tree-line-receipt';
  if (normalized.includes('partnerexport')) return 'partner-export';
  if (normalized.includes('applicationstatus')) return 'application-status';

  return null;
}

const createEmptyAdminApplicationExportSubmitState = (): AdminApplicationExportSubmitState => ({
  request: {
    Email: '',
    Program: ProgramNames.TreeCityUSA,
    ApplicationType: ApplicationTypes.Standard,
    Year: new Date().getFullYear(),
    IncludeQuestionResponses: false,
    StateId: null,
    CountyId: null,
    CountryId: null,
    PartnerIds: [],
  },
  isValid: false,
});

const createEmptyNewUserJoinedPartnerGroupSubmitState =
  (): NewUserJoinedPartnerGroupSubmitState => ({
    request: {
      Email: '',
      NewUserEmail: '',
      PartnerGroupName: '',
    },
    isValid: false,
  });

const createEmptyNewReviewerInvitationSubmitState = (): NewReviewerInvitationSubmitState => ({
  request: {
    Email: '',
    UserRole: UserType.Applicant,
    FullName: '',
  },
  isValid: false,
});

const createEmptyTreeLineReceiptSubmitState = (
  defaultLocale: string
): TreeLineReceiptSubmitState => ({
  applicationId: 0,
  email: '',
  year: new Date().getFullYear(),
  utilityName: '',
  paymentType: PaymentType.CreditCard,
  amountPaid: 0,
  paymentDate: '',
  transactionId: '',
  customerId: '',
  localeCode: defaultLocale,
  manualBillingAddress: {
    billingFullName: '',
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingStateCode: '',
    billingPostalCode: '',
  },
  isValid: false,
});

const createEmptyPartnerExportSubmitState = (): PartnerExportSubmitState => ({
  request: {
    Program: ProgramNames.TreeCityUSA,
  },
  isValid: false,
});

const createEmptyApplicationStatusSubmitState = (
  defaultLocale: string
): ApplicationStatusSubmitState => ({
  request: {
    Email: '',
    ApplicationId: 0,
    Program: ProgramNames.TreeCityUSA,
    PartnerName: '',
    RecognitionYear: new Date().getFullYear(),
    LocaleCode: defaultLocale,
    HasStateCoordinator: false,
    HasRegionalCoordinator: false,
    HasNationalProgrammeLead: false,
    NewStatus: ApplicationStatus.Submitted,
    RevisionStatus: null,
    IsResubmitted: false,
    ApplicationType: ApplicationTypes.Standard,
  },
  isValid: false,
});

const SendEmail: React.FC = () => {
  const { setAlert } = useContext(SnackbarContext);
  const { userCurrent } = useContext(UsersContext);
  const {
    getEmailTypes,
    sendAdminApplicationExportEmail,
    sendNewUserJoinedPartnerGroupEmail,
    sendNewReviewerInvitationEmail,
    sendTreeLineReceiptEmail,
    sendPartnerExportEmail,
    sendApplicationStatusEmail,
  } = useEmailRecord();
  const { getTreeLineReceiptDetails } = useApplication();

  const [emailTypes, setEmailTypes] = useState<EmailTypeOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState<boolean>(true);
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [sendCopyToSelf, setSendCopyToSelf] = useState<boolean>(false);

  const [selectedEmailTypeId, setSelectedEmailTypeId] = useState<string>('');

  const [adminApplicationExportSubmitState, setAdminApplicationExportSubmitState] =
    useState<AdminApplicationExportSubmitState>(createEmptyAdminApplicationExportSubmitState());
  const [adminApplicationExportResetSignal, setAdminApplicationExportResetSignal] =
    useState<number>(0);

  const [newUserJoinedPartnerGroupSubmitState, setNewUserJoinedPartnerGroupSubmitState] =
    useState<NewUserJoinedPartnerGroupSubmitState>(
      createEmptyNewUserJoinedPartnerGroupSubmitState()
    );
  const [newUserJoinedPartnerGroupResetSignal, setNewUserJoinedPartnerGroupResetSignal] =
    useState<number>(0);

  const [newReviewerInvitationSubmitState, setNewReviewerInvitationSubmitState] =
    useState<NewReviewerInvitationSubmitState>(createEmptyNewReviewerInvitationSubmitState());
  const [newReviewerInvitationResetSignal, setNewReviewerInvitationResetSignal] =
    useState<number>(0);

  const [treeLineReceiptSubmitState, setTreeLineReceiptSubmitState] =
    useState<TreeLineReceiptSubmitState>(createEmptyTreeLineReceiptSubmitState(DEFAULT_LOCALE));
  const [treeLineResetSignal, setTreeLineResetSignal] = useState<number>(0);

  const [partnerExportSubmitState, setPartnerExportSubmitState] =
    useState<PartnerExportSubmitState>(createEmptyPartnerExportSubmitState());
  const [partnerExportResetSignal, setPartnerExportResetSignal] = useState<number>(0);

  const [applicationStatusSubmitState, setApplicationStatusSubmitState] =
    useState<ApplicationStatusSubmitState>(createEmptyApplicationStatusSubmitState(DEFAULT_LOCALE));
  const [applicationStatusResetSignal, setApplicationStatusResetSignal] = useState<number>(0);

  useEffect(() => {
    const loadEmailTypes = async () => {
      try {
        const types = await getEmailTypes();
        setEmailTypes(types ?? []);
      } catch {
        setAlert({
          severity: 'error',
          message: 'Unable to load email types.',
        });
      } finally {
        setLoadingTypes(false);
      }
    };

    void loadEmailTypes();
  }, [getEmailTypes, setAlert]);

  const selectedEmailType = useMemo(
    () => emailTypes.find((type) => String(type.id) === selectedEmailTypeId) ?? null,
    [emailTypes, selectedEmailTypeId]
  );

  const selectedEmailTypeKey = useMemo(
    () => (selectedEmailType != null ? resolveEmailTypeKey(selectedEmailType.name) : null),
    [selectedEmailType]
  );

  const handleTreeLineLoadError = useCallback(() => {
    setAlert({
      severity: 'error',
      message: 'Unable to auto-populate Tree Line Receipt details from Application Id.',
    });
  }, [setAlert]);

  const isTreeLineReceiptValid = treeLineReceiptSubmitState.isValid;
  const isAdminApplicationExportValid = adminApplicationExportSubmitState.isValid;
  const isNewUserJoinedPartnerGroupValid = newUserJoinedPartnerGroupSubmitState.isValid;
  const isNewReviewerInvitationValid = newReviewerInvitationSubmitState.isValid;
  const isPartnerExportValid = partnerExportSubmitState.isValid;
  const isApplicationStatusValid = applicationStatusSubmitState.isValid;

  const canSend =
    selectedEmailTypeKey != null &&
    !sendingEmail &&
    ((selectedEmailTypeKey === 'admin-application-export' && isAdminApplicationExportValid) ||
      (selectedEmailTypeKey === 'new-user-joined-partner-group' &&
        isNewUserJoinedPartnerGroupValid) ||
      (selectedEmailTypeKey === 'new-reviewer-invitation' && isNewReviewerInvitationValid) ||
      (selectedEmailTypeKey === 'tree-line-receipt' && isTreeLineReceiptValid) ||
      (selectedEmailTypeKey === 'partner-export' && isPartnerExportValid) ||
      (selectedEmailTypeKey === 'application-status' && isApplicationStatusValid));

  const handleEmailTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedEmailTypeId(event.target.value);
  };

  const handleClearForm = () => {
    setSelectedEmailTypeId('');
    setSendCopyToSelf(false);
    setAdminApplicationExportSubmitState(createEmptyAdminApplicationExportSubmitState());
    setAdminApplicationExportResetSignal((prev) => prev + 1);
    setNewUserJoinedPartnerGroupSubmitState(createEmptyNewUserJoinedPartnerGroupSubmitState());
    setNewUserJoinedPartnerGroupResetSignal((prev) => prev + 1);
    setNewReviewerInvitationSubmitState(createEmptyNewReviewerInvitationSubmitState());
    setNewReviewerInvitationResetSignal((prev) => prev + 1);
    setTreeLineReceiptSubmitState(createEmptyTreeLineReceiptSubmitState(DEFAULT_LOCALE));
    setTreeLineResetSignal((prev) => prev + 1);
    setPartnerExportSubmitState(createEmptyPartnerExportSubmitState());
    setPartnerExportResetSignal((prev) => prev + 1);
    setApplicationStatusSubmitState(createEmptyApplicationStatusSubmitState(DEFAULT_LOCALE));
    setApplicationStatusResetSignal((prev) => prev + 1);
  };

  const handleSendEmail = async () => {
    if (!canSend || selectedEmailTypeKey == null) {
      return;
    }

    const loggedInUserEmail = userCurrent?.email?.trim() ?? '';
    const supportsCopyToSelf =
      selectedEmailTypeKey !== 'partner-export' && selectedEmailTypeKey !== 'tree-line-receipt';

    if (sendCopyToSelf && supportsCopyToSelf && loggedInUserEmail === '') {
      setAlert({
        severity: 'error',
        message: 'Unable to send a copy because the logged-in user email is unavailable.',
      });
      return;
    }

    setSendingEmail(true);

    try {
      let response = null;

      const sendWithOptionalCopy = async <TRequest extends { Email?: string | null }>(
        send: (request: TRequest) => Promise<{ message?: string } | null>,
        request: TRequest
      ) => {
        const primaryResponse = await send(request);

        if (sendCopyToSelf) {
          const originalEmail = request.Email?.trim().toLowerCase() ?? '';
          const copyEmail = loggedInUserEmail.toLowerCase();

          if (originalEmail !== copyEmail) {
            await send({
              ...request,
              Email: loggedInUserEmail,
            });
          }
        }

        return primaryResponse;
      };

      switch (selectedEmailTypeKey) {
        case 'admin-application-export':
          response = await sendWithOptionalCopy(
            sendAdminApplicationExportEmail,
            adminApplicationExportSubmitState.request
          );
          break;
        case 'new-user-joined-partner-group':
          response = await sendWithOptionalCopy(
            sendNewUserJoinedPartnerGroupEmail,
            newUserJoinedPartnerGroupSubmitState.request
          );
          break;
        case 'new-reviewer-invitation':
          response = await sendWithOptionalCopy(
            sendNewReviewerInvitationEmail,
            newReviewerInvitationSubmitState.request
          );
          break;
        case 'tree-line-receipt':
          response = await sendTreeLineReceiptEmail({
            UtilityName: treeLineReceiptSubmitState.utilityName,
            Year: treeLineReceiptSubmitState.year,
            PaymentType: treeLineReceiptSubmitState.paymentType,
            AmountPaid: treeLineReceiptSubmitState.amountPaid,
            PaymentDate: treeLineReceiptSubmitState.paymentDate,
            TransactionId: treeLineReceiptSubmitState.transactionId,
            CustomerId: treeLineReceiptSubmitState.customerId,
            Email: treeLineReceiptSubmitState.email,
            BillingFullName: treeLineReceiptSubmitState.manualBillingAddress.billingFullName,
            LocaleCode: treeLineReceiptSubmitState.localeCode,
            BillingAddress_Address1:
              treeLineReceiptSubmitState.manualBillingAddress.billingAddress1,
            BillingAddress_Address2:
              treeLineReceiptSubmitState.manualBillingAddress.billingAddress2,
            BillingAddress_City: treeLineReceiptSubmitState.manualBillingAddress.billingCity,
            BillingAddress_StateCode:
              treeLineReceiptSubmitState.manualBillingAddress.billingStateCode,
            BillingAddress_PostalCode:
              treeLineReceiptSubmitState.manualBillingAddress.billingPostalCode,
          });
          break;
        case 'partner-export':
          response = await sendPartnerExportEmail(partnerExportSubmitState.request);
          break;
        case 'application-status':
          response = await sendWithOptionalCopy(
            sendApplicationStatusEmail,
            applicationStatusSubmitState.request
          );
          break;
      }

      setAlert({
        severity: 'success',
        message: response?.message ?? 'Email sent successfully.',
      });
    } catch (error) {
      console.error(error);
      setAlert({
        severity: 'error',
        message: 'Unable to send email. Please try again.',
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const renderFollowUpForm = () => {
    if (selectedEmailTypeKey == null) {
      return (
        <Typography variant='subtitle3'>No form mapping found for this email type yet.</Typography>
      );
    }

    switch (selectedEmailTypeKey) {
      case 'admin-application-export':
        return (
          <AdminApplicationExportSection
            onStateChange={setAdminApplicationExportSubmitState}
            resetSignal={adminApplicationExportResetSignal}
          />
        );

      case 'new-user-joined-partner-group':
        return (
          <NewUserJoinedPartnerGroupSection
            onStateChange={setNewUserJoinedPartnerGroupSubmitState}
            resetSignal={newUserJoinedPartnerGroupResetSignal}
          />
        );

      case 'new-reviewer-invitation':
        return (
          <NewReviewerInvitationSection
            onStateChange={setNewReviewerInvitationSubmitState}
            resetSignal={newReviewerInvitationResetSignal}
          />
        );

      case 'tree-line-receipt':
        return (
          <TreeLineReceiptSection
            isActive={selectedEmailTypeKey === 'tree-line-receipt'}
            defaultLocale={DEFAULT_LOCALE}
            localeOptions={localeOptions}
            getTreeLineReceiptDetails={getTreeLineReceiptDetails}
            onLoadError={handleTreeLineLoadError}
            onStateChange={setTreeLineReceiptSubmitState}
            resetSignal={treeLineResetSignal}
          />
        );

      case 'partner-export':
        return (
          <PartnerExportSection
            onStateChange={setPartnerExportSubmitState}
            resetSignal={partnerExportResetSignal}
          />
        );

      case 'application-status':
        return (
          <ApplicationStatusSection
            localeOptions={localeOptions}
            defaultLocale={DEFAULT_LOCALE}
            onStateChange={setApplicationStatusSubmitState}
            resetSignal={applicationStatusResetSignal}
          />
        );
    }
  };

  if (loadingTypes) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 900 }}>
      <Stack spacing={3}>
        <Typography variant='h3'>Send Email</Typography>

        <FormControl fullWidth>
          <InputLabel id='email-type-label' required>
            Email Type
          </InputLabel>
          <Select
            labelId='email-type-label'
            id='email-type-select'
            value={selectedEmailTypeId}
            label='Email Type'
            onChange={handleEmailTypeChange}
          >
            {emailTypes.map((type) => (
              <MenuItem key={String(type.id)} value={String(type.id)}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedEmailType != null && renderFollowUpForm()}

        <FormControlLabel
          control={
            <Checkbox
              checked={sendCopyToSelf}
              onChange={(e) => setSendCopyToSelf(e.target.checked)}
            />
          }
          label='Send copy to yourself'
        />

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button variant='secondary' onClick={handleClearForm} disabled={sendingEmail}>
            Clear Form
          </Button>
          <Button variant='primary' onClick={handleSendEmail} disabled={!canSend}>
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default SendEmail;
